'use client';

import type { SnackbarKey, OptionsObject } from 'notistack';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Button } from '@petsmart-ui/sparky';
import { FadeSlide } from '@libs/common-next';

/**
 * Module augmentation for notistack's OptionsObject to allow a custom 'twoLine' variant.
 */
declare module 'notistack' {
    interface OptionsObject {
        variant?:
            | 'default'
            | 'error'
            | 'success'
            | 'warning'
            | 'info'
            | 'twoLine';
    }
}

interface TwoLineSnackbarOptions extends OptionsObject {
    variant?: 'default' | 'error' | 'success' | 'warning' | 'info' | 'twoLine';
    secondaryText: string;
    actionText?: string;
    onClick?: () => void;
}

type SnackbarProps = {
    primaryText: string;
    secondaryText?: string;
    actionText?: string;
    onClick?: () => void;
    autoDismiss?: boolean;
};

const ACTION_STYLES = Object.freeze({
    fontWeight: 700,
    color: '#56B3FF',
    background: 'transparent',
    outline: 'none',
} as const);

const BANNER_STYLES = Object.freeze({
    width: '343px',
    fontFamily: 'Euclid, sans-serif',
    backgroundColor: '#333333',
    color: 'white',
    margin: '0 auto',
} as const);

const handleActionClick = (snackbarId: SnackbarKey, onClick?: () => void) => {
    if (onClick) {
        onClick();
        closeSnackbar(snackbarId);
    }
};

export const SnackbarUtils = {
    show({
        primaryText,
        secondaryText,
        actionText,
        onClick,
        autoDismiss = true,
    }: SnackbarProps) {
        const commonOptions: OptionsObject = {
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            TransitionComponent: FadeSlide,
            autoHideDuration: autoDismiss ? 5000 : null,
        };

        if (secondaryText) {
            const snackbarOptions: TwoLineSnackbarOptions = {
                ...commonOptions,
                variant: 'twoLine',
                secondaryText,
                actionText,
                onClick,
            };
            enqueueSnackbar(primaryText, snackbarOptions);
        } else {
            const snackbarOptions: OptionsObject = {
                ...commonOptions,
                variant: 'default',
                action: snackbarId =>
                    actionText ? (
                        <Button
                            style={ACTION_STYLES}
                            onClick={() =>
                                handleActionClick(snackbarId, onClick)
                            }
                            text={actionText}
                            variant="link"
                        />
                    ) : null,
                style: BANNER_STYLES,
            };
            enqueueSnackbar(primaryText, snackbarOptions);
        }
    },
};
